<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import AppSidebar from '@/components/layout/AppSidebar.vue'
import AppTopbar from '@/components/layout/AppTopbar.vue'

type RouteName = 'products' | 'upload' | 'adhoc' | 'reports' | 'userReporting' | 'profileReporting' | 'settings'

const router = useRouter()
const authStore = useAuthStore()

const isAuthenticated = computed<boolean>(() => authStore.isAuthenticated as boolean)
const isSidebarCollapsed = ref<boolean>(false)

const handleSidebarCollapse = (collapsed: boolean): void => {
  isSidebarCollapsed.value = collapsed
}

const showLayout = computed<boolean>(() => {
  const route = router.currentRoute.value
  return route.name !== 'login' && route.name !== 'authCallback'
})

// A protected page only ever renders inside the authenticated layout. Logging out drops that
// layout before the browser leaves, and rendering the page again without it fired its data
// requests with no token — whose 401s started a second logout that cancelled the first.
const requiresAuth = computed<boolean>(() => Boolean(router.currentRoute.value.meta.requiresAuth))

const pageTitle = computed<string>(() => {
  const route = router.currentRoute.value
  const titles: Record<RouteName, string> = {
    products: 'Products',
    upload: 'Upload',
    adhoc: 'Ad-Hoc Classification',
    reports: 'Reports',
    userReporting: 'User Reports',
    profileReporting: 'Profile Reports',
    settings: 'Settings'
  }
  return titles[route.name as RouteName] || 'Avalara Trade Compliance'
})

// Idle timeout handling
let idleTimer: ReturnType<typeof setTimeout> | null = null
const IDLE_TIMEOUT = 30 * 60 * 1000 // 30 minutes

const resetIdleTimer = (): void => {
  if (idleTimer) clearTimeout(idleTimer)
  idleTimer = null
  if (authStore.isAuthenticated) {
    idleTimer = setTimeout(() => {
      authStore.logout('Your session has timed out!')
    }, IDLE_TIMEOUT)
  }
}

const idleEvents: string[] = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart']

// Safety net for the redirect-to-login-after-logout flow. `logout()` navigates away with
// window.location.href, but if the browser later restores this page from bfcache (back/forward
// button) the JS state comes back as it was before that navigation ran. pageshow fires on that
// restore even though no Vue lifecycle hook does, so it's the one place we can catch it.
const handlePageShow = (event: PageTransitionEvent): void => {
  if (sessionStorage.getItem('logged_out') === 'true') {
    sessionStorage.removeItem('logged_out')
    router.push('/login')
  } else if (event.persisted && !authStore.isAuthenticated) {
    router.push('/login')
  }
}

watch(
  () => authStore.isAuthenticated,
  (authenticated) => {
    if (authenticated) {
      resetIdleTimer()
    } else if (idleTimer) {
      clearTimeout(idleTimer)
      idleTimer = null
    }
  }
)

onMounted(() => {
  // Setup idle detection
  idleEvents.forEach(event => {
    document.addEventListener(event, resetIdleTimer, true)
  })
  resetIdleTimer()
  window.addEventListener('pageshow', handlePageShow)
})

onUnmounted(() => {
  if (idleTimer) clearTimeout(idleTimer)
  idleEvents.forEach(event => {
    document.removeEventListener(event, resetIdleTimer, true)
  })
  window.removeEventListener('pageshow', handlePageShow)
})
</script>

<template>
  <div class="app-wrapper">
    <!-- Authenticated Layout with Sidebar -->
    <template v-if="showLayout && isAuthenticated">
      <AppSidebar @collapse-change="handleSidebarCollapse" />
      <div :class="['main-wrapper', { 'sidebar-collapsed': isSidebarCollapsed }]">
        <AppTopbar>
          <template #title>{{ pageTitle }}</template>
        </AppTopbar>
        <main class="main-content">
          <RouterView />
        </main>
      </div>
    </template>
    
    <!-- Unauthenticated Layout (Login, Callback) -->
    <template v-else-if="!requiresAuth">
      <RouterView />
    </template>
  </div>
</template>

<style scoped>
.app-wrapper {
  display: flex;
  min-height: 100vh;
  background: #f1f5f9;
}

.main-wrapper {
  flex: 1;
  /* Keep content offset only by the collapsed sidebar width (72px).
     When the sidebar is expanded, it should overlay content instead of pushing it. */
  margin-left: 72px;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  transition: margin-left 0.3s ease;
}

.main-wrapper.sidebar-collapsed {
  margin-left: 72px;
}

.main-content {
  flex: 1;
  overflow: auto;
}
</style>
